import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@shared/state';
import { MobileBottomSheet } from '@shared/ui/mobile';
import { FabMenuContext, type FabMenuContextValue, type FabMenuOrigin } from './fabMenuContext';
import { useFabMenu } from './useFabMenu';
import { ToolIcon } from '../ToolIcon';
import { PrimaryIcon } from '../PrimaryIcon';
import { SECONDARY_MOBILE_NAV_ITEMS } from '../navigation';
import { FAB_OPTIONS, MOBILE_CREATE_ACTIONS } from './fabMenuOptions';

export { FAB_OPTIONS } from './fabMenuOptions';

export function FabMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [origin, setOrigin] = useState<FabMenuOrigin | null>(null);
  const value = useMemo<FabMenuContextValue>(
    () => ({
      open,
      origin,
      setOpen,
      toggle: (nextOrigin: FabMenuOrigin) => {
        setOpen((current) => {
          const next = !current;
          setOrigin(next ? nextOrigin : null);
          return next;
        });
      },
    }),
    [open, origin],
  );

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    if (!open) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return <FabMenuContext.Provider value={value}>{children}</FabMenuContext.Provider>;
}

function FabMenuOptionList({
  showSecondaryDestinations,
  actions,
  mode,
  onOption,
}: {
  showSecondaryDestinations: boolean;
  actions: typeof FAB_OPTIONS;
  mode: 'guided' | 'expert';
  onOption: (to: string) => void;
}) {
  return (
    <>
      {showSecondaryDestinations && (
        <div className="fab-menu-section">
          <p className="fab-menu-section-label">Aller à</p>
          <div className="fab-menu-list">
            {SECONDARY_MOBILE_NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="fab-menu-item"
                onClick={() => onOption(item.to)}
              >
                <span className="fab-menu-icon"><PrimaryIcon id={item.id} size={18} /></span>
                <span className="fab-menu-copy">
                  <strong>{item.label}</strong>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="fab-menu-section">
        {showSecondaryDestinations && <p className="fab-menu-section-label">Créer</p>}
        <div className="fab-menu-list">
          {actions.map((item) => (
            <button
              key={item.id}
              type="button"
              className="fab-menu-item"
              onClick={() => onOption(item.to)}
            >
              <span className="fab-menu-icon"><ToolIcon icon={item.icon} size={18} /></span>
              <span className="fab-menu-copy">
                <strong>{item.label}</strong>
                <span>{mode === 'expert' ? `${item.description} · accès direct` : item.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function FabMenu() {
  const navigate = useNavigate();
  const profile = useProfile();
  const { open, origin, setOpen } = useFabMenu();
  const mode = profile.mode || 'guided';
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  // `origin` repasse à `null` dès la fermeture (voir FabMenuProvider.toggle) :
  // on retient donc le dernier variant connu pour continuer à rendre
  // `MobileBottomSheet` (avec `open=false`) le temps qu'elle referme
  // proprement — sans quoi FabMenu la démonterait avant qu'elle ait pu
  // restaurer le focus/déverrouiller le scroll (React ne rejoue pas l'effet
  // "fermeture" d'un composant qui disparaît de l'arbre, seulement son
  // nettoyage).
  const lastOriginRef = useRef<FabMenuOrigin>('rail');
  if (origin) lastOriginRef.current = origin;
  const isMobileSheet = lastOriginRef.current === 'bottom';
  // Sur mobile ("bottom"), ces destinations ne sont plus dans la barre
  // (elle en compte 5 depuis MOB-011, mais Documents/Profil restent dans le
  // menu secondaire) : on les propose ici, dans la même feuille que la
  // création, pour éviter un second contrôle. Sur desktop ("rail"), elles
  // sont déjà visibles en permanence — inutile de les répéter.
  const showSecondaryDestinations = isMobileSheet && SECONDARY_MOBILE_NAV_ITEMS.length > 0;

  useEffect(() => {
    // La feuille mobile gère son propre clic sur backdrop (MobileBottomSheet) —
    // cette fermeture au clic extérieur ne s'applique qu'au menu desktop.
    if (isMobileSheet) return undefined;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-fab-menu], [data-fab-trigger]')) return;
      setOpen(false);
    }

    if (!open) return undefined;
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, setOpen, isMobileSheet]);

  // Focus géré manuellement (Phase G) : à l'ouverture, on mémorise le
  // déclencheur et on envoie le focus dans le menu ; à la fermeture (Échap,
  // option choisie, clic à l'extérieur), le focus revient sur le
  // déclencheur d'origine plutôt que de se perdre sur le document.
  // Sur mobile, MobileBottomSheet gère déjà ce cycle — éviter la double
  // gestion (deux `requestAnimationFrame` concurrents sur le même open).
  useEffect(() => {
    if (isMobileSheet) return undefined;
    if (open) {
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = requestAnimationFrame(() => closeButtonRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    lastFocusedRef.current?.focus();
    return undefined;
  }, [open, isMobileSheet]);

  function close() {
    setOpen(false);
  }

  function handleOption(to: string) {
    close();
    navigate(to);
  }

  if (isMobileSheet) {
    // Toujours rendue (avec `open` en prop) plutôt que démontée ici quand
    // `open` est faux : `MobileBottomSheet` a besoin de voir passer cette
    // transition pour restaurer le focus et déverrouiller le scroll (voir
    // commentaire sur `lastOriginRef` ci-dessus).
    return (
      <MobileBottomSheet open={open} onClose={close} title="Créer">
        <FabMenuOptionList
          showSecondaryDestinations={showSecondaryDestinations}
          actions={MOBILE_CREATE_ACTIONS}
          mode={mode}
          onOption={handleOption}
        />
      </MobileBottomSheet>
    );
  }

  if (!open) return null;

  return (
    <>
      <div className="fab-backdrop" />
      <section className="fab-menu card" data-fab-menu aria-label="Accès rapide">
        <div className="fab-menu-head">
          <p className="page-kicker">Accès rapide</p>
          <button ref={closeButtonRef} type="button" className="fab-menu-close" onClick={close} aria-label="Fermer le menu">
            ×
          </button>
        </div>

        <FabMenuOptionList
          showSecondaryDestinations={showSecondaryDestinations}
          actions={FAB_OPTIONS}
          mode={mode}
          onOption={handleOption}
        />
      </section>
    </>
  );
}
